import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, updateProfile, changePassword } from '../../api';
import Header from '../Header';
import './Auth.css';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [passwordSection, setPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const userData = await getProfile();
        setUser(userData);
        setName(userData.name);
        setEmail(userData.email);
      } catch (err) {
        setError('Failed to load profile. Please try again.');
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      await updateProfile({ name, email });
      setUser({ ...user, name, email });
      setSuccess('Profile updated successfully');
      setEditMode(false);
      
      // Update local storage user data
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        userData.name = name;
        userData.email = email;
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setPasswordSection(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password. Please try again.');
    }
  };

  const cancelEdit = () => {
    setName(user.name);
    setEmail(user.email);
    setEditMode(false);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="auth-container">
          <div className="auth-card">
            <h2>Profile</h2>
            <div className="loading-spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="auth-container">
        <div className="auth-card profile-card">
          <h2>My Profile</h2>
          
          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}
          
          <div className="profile-avatar">
            <div className="avatar-circle">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            {!editMode && (
              <button 
                className="edit-profile-button" 
                onClick={() => setEditMode(true)}
              >
                <FaEdit /> Edit Profile
              </button>
            )}
          </div>
          
          {editMode ? (
            <form onSubmit={handleProfileUpdate} className="auth-form">
              <div className="form-group">
                <div className="input-icon-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Username"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <div className="input-icon-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email Address"
                    required
                  />
                </div>
              </div>
              
              <div className="profile-actions">
                <button type="submit" className="auth-button save-button">
                  <FaSave /> Save Changes
                </button>
                <button 
                  type="button" 
                  className="auth-button cancel-button"
                  onClick={cancelEdit}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info">
              <div className="info-item">
                <span className="info-label">Name:</span>
                <span className="info-value">{user?.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{user?.email}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Member Since:</span>
                <span className="info-value">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          )}
          
          {!editMode && (
            <div className="password-section">
              <button 
                className="change-password-button"
                onClick={() => setPasswordSection(!passwordSection)}
              >
                <FaLock /> {passwordSection ? 'Cancel Password Change' : 'Change Password'}
              </button>
              
              {passwordSection && (
                <form onSubmit={handlePasswordChange} className="auth-form">
                  {passwordError && <div className="auth-error">{passwordError}</div>}
                  {passwordSuccess && <div className="auth-success">{passwordSuccess}</div>}
                  
                  <div className="form-group">
                    <div className="input-icon-wrapper">
                      <FaLock className="input-icon" />
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current Password"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <div className="input-icon-wrapper">
                      <FaLock className="input-icon" />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New Password"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <div className="input-icon-wrapper">
                      <FaLock className="input-icon" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm New Password"
                        required
                      />
                      <button 
                        type="button" 
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <button type="submit" className="auth-button">
                    Update Password
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Profile; 